'use client';

import Image from '@/components/common/image/Image';
import PortableText from '@/components/portableText';
import ErrorComponent from '@/components/empty/ErrorComponent';
import Loading from '@/components/common/loading/Loading';
import Divider from '@/components/common/divider/Divider';
import PullQuote from '@/components/common/pullQuote/PullQuote';
import ReadMoreContainer from './ReadMoreContainer';
import useReleaseBySlug from '@/utils/hooks/useReleaseBySlug';
import ShareThisArticle from './ShareThisArticle';
import { SHORT_DATE } from '@/constants/dates';
import { FALLBACK_IMAGE } from '@/constants/media';
import { formatDate } from '@/utils/date';

type ReleaseProps = {
  slug: string;
  isDraft: boolean;
};

const ReleaseContainer = ({ slug, isDraft }: ReleaseProps) => {
  const { data, isLoading, isError } = useReleaseBySlug({ slug, isDraft });
  const releaseData = data ?? {};

  if (isError) {
    return (
      <div className='min-h-[400px] flex justify-center items-center'>
        <ErrorComponent />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='min-h-[400px] flex justify-center items-center'>
        <Loading />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      <div className='text-center text-grey-d1'>
        {formatDate(releaseData?.datePublished, SHORT_DATE)}
      </div>
      <h1 className='text-center text-content-default text-xl md:text-4xl font-bold'>
        {releaseData.title}
      </h1>
      {releaseData.title && releaseData?.slug?.current ? (
        <ShareThisArticle
          title={releaseData.title}
          slug={releaseData.slug.current}
        />
      ) : null}
      <div className='text-center'>
        <em>{releaseData?.subheading}</em>
      </div>
      <section>
        <Image
          priority
          width={616}
          height={306}
          src={releaseData.image?.asset?.url || FALLBACK_IMAGE}
          alt={releaseData.title ?? 'FanDuel Logo'}
          className='object-contain rounded mx-auto'
          sizes='(min-width: 1440px) 66vw, (min-width: 1024px) 50vw, (min-width: 768px) 100vw, 100vw'
        />
        {releaseData.pullQuote ? (
          <aside className='mt-8 lg:mt-4 lg:float-left lg:mr-4 lg:w-[300px]'>
            <PullQuote>{releaseData.pullQuote}</PullQuote>
          </aside>
        ) : null}
        <PortableText value={releaseData.bodyRaw} />
      </section>
      <Divider />
      <h2 className='text-xl leading-5 font-bold my-3 lg:text-2xl lg:leading-8 lg:my-4'>
        Read More
      </h2>
      <ReadMoreContainer currentSlug={slug} />
    </div>
  );
};

export default ReleaseContainer;
